import moment from "moment";

/** Normalize HHmm / HH:mm / HH:mm:ss to a moment on the given date. */
export const getSessionDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr).format("YYYY-MM-DD");
  if (!timeStr) return moment(date, "YYYY-MM-DD");

  const raw = String(timeStr).trim();
  let timeFormatted = raw;
  if (/^\d{4}$/.test(raw)) {
    timeFormatted = `${raw.substring(0, 2)}:${raw.substring(2, 4)}:00`;
  } else if (/^\d{2}:\d{2}$/.test(raw)) {
    timeFormatted = `${raw}:00`;
  }

  return moment(`${date} ${timeFormatted}`, "YYYY-MM-DD HH:mm:ss");
};

/**
 * Resolve join URL from session / course_date shapes used across tenants.
 * Prefer `meeting_url`, fall back to TMS `online_meeting_details.join_url`.
 */
export const resolveMeetingUrl = (session) => {
  const direct = String(session?.meeting_url || "").trim();
  if (direct) return direct;
  const nested = session?.online_meeting_details?.join_url;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  return "";
};

/**
 * Label + state for the online join control.
 * - Ended after session end
 * - Join Now from 10 minutes before start (and during session)
 * - Otherwise countdown: In X days / hours / minutes
 */
export const getJoinButtonState = (session, now = moment()) => {
  const meetingUrl = resolveMeetingUrl(session);
  if (!meetingUrl) {
    return { meetingUrl: "", status: "none", label: null };
  }

  const start = getSessionDateTime(session.date, session.start_time);
  const end = getSessionDateTime(session.date, session.end_time) || start?.clone().add(1, "hour");

  if (!start) {
    return { meetingUrl, status: "join", label: "Join Now" };
  }

  if (end && now.isAfter(end)) {
    return { meetingUrl, status: "ended", label: "Ended" };
  }

  const minutesUntilStart = start.diff(now, "minutes");
  if (minutesUntilStart <= 10) {
    return { meetingUrl, status: "join", label: "Join Now" };
  }

  const days = start.diff(now, "days");
  if (days >= 1) {
    return {
      meetingUrl,
      status: "countdown",
      label: `In ${days} ${days === 1 ? "day" : "days"}`,
    };
  }

  const hours = start.diff(now, "hours");
  if (hours >= 1) {
    return {
      meetingUrl,
      status: "countdown",
      label: `In ${hours} ${hours === 1 ? "hour" : "hours"}`,
    };
  }

  return {
    meetingUrl,
    status: "countdown",
    label: `In ${minutesUntilStart} ${minutesUntilStart === 1 ? "minute" : "minutes"}`,
  };
};

export const openMeetingLink = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const copyMeetingLink = async (url) => {
  if (!url) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const input = document.createElement("textarea");
    input.value = url;
    input.setAttribute("readonly", "");
    input.style.position = "absolute";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(input);
    return ok;
  } catch {
    return false;
  }
};
